import * as React from "react";
import { Input } from "antd";
import { ChevronDown, ChevronUp } from "react-feather";
import { SyntaxHighlight } from "../../components/syntaxHighlight/SyntaxHighlight";
import { ExternalLink } from "../../newcomponents/table/components/ExternalLink";
import { CopyButtonWithApproval } from "../domain/create/copyButtonWithApproval";
import { IS_DEPLOYMENT_ENV_TEST } from "../../envVariables";
import { useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";

export interface AWSInstructions {
  message: string;
  data: {
    deployRole: string;
    trustPolicy: any;
    connectorPolicy: any;
    accessPolicies: string[];
    cli: string[];
  };
}

interface Props {
  instructions: AWSInstructions;
}
export const ProviderAWSInstructions: React.FC<Props> = ({ instructions }) => {
  const org = useAppSelector(selectOrg);
  const [state, _setState] = React.useState<"none" | "cli" | "ui">("none");

  const defaultRoleName = `cpln-mk8s-${org}`;
  const [accountId, setAccountId] = React.useState("");
  const [roleName, setRoleName] = React.useState(defaultRoleName);

  function setState(from: "cli" | "ui") {
    if (state === from) {
      _setState("none");
    } else {
      _setState(from);
    }
  }

  return (
    <>
      <div className="mt-1">
        <button type={"button"} className="block ngfocus color-link" onClick={() => setState("cli")}>
          How do I get my AWS Deploy Role ARN with AWS CLI?{" "}
          {state === "cli" ? (
            <ChevronUp style={{ transform: "translateY(2px)" }} className="feather-icon inline-block" />
          ) : (
            <ChevronDown style={{ transform: "translateY(2px)" }} className="feather-icon inline-block" />
          )}
        </button>

        {state === "cli" ? (
          <ul className="mt-1 mb-4 px-4 py-2 ml-4">
            <li className="my-2 list-decimal">
              Sign into the <ExternalLink url="https://console.aws.amazon.com" label="AWS Console" disableCopy />.
            </li>
            <li className="my-2 list-decimal">
              Click the dropdown next to your <span className="font-semibold">username</span>.
            </li>
            <li className="my-2 list-decimal">
              Copy the <span className="font-semibold">Account ID</span> next to "My Account".
            </li>
            <div className="flex items-center justify-center my-2 border" style={{ width: 421, height: 358 }}>
              <img
                className="object-container"
                style={{ width: 421, height: 358 }}
                src={"/resources/howto/aws_accountIdSteps.jpg"}
                alt={"how do I get my aws account id?"}
              />
            </div>
            <li className="mb-2 list-decimal">Paste it in the field below to continue with the guide.</li>
            <Input
              style={{ width: 450 }}
              placeholder="AWS Account Id"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            />
            {!accountId ? null : (
              <>
                <li className="my-2 list-decimal">
                  Optionally, customize the role name (default: <span className="font-semibold">{defaultRoleName}</span>
                  ).
                </li>
                <Input
                  style={{ width: 450 }}
                  placeholder="Role Name"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                />
                <li className="list-decimal my-2">
                  Install the <ExternalLink url="https://aws.amazon.com/cli" label="AWS CLI" disableCopy /> and{" "}
                  <ExternalLink
                    url="https://docs.aws.amazon.com/cli/latest/userguide/cli-chap-configure.html"
                    label="configure authentication"
                    disableCopy
                  />
                  .
                </li>
                <li className="my-2 list-decimal">
                  Execute the following commands to create the role and set the policies.
                </li>
                <SyntaxHighlight
                  code={instructions.data.cli
                    .map((_cliStep) => {
                      let step = _cliStep;
                      if (accountId) step = step.replace("$ACCOUNT_ID", accountId);
                      if (roleName !== defaultRoleName) step = step.replaceAll(defaultRoleName, roleName);
                      // Add --no-cli-pager to prevent pager from stopping execution
                      if (step.startsWith("aws ")) {
                        step = step.replace("aws ", "aws --no-cli-pager ");
                      }
                      // Continue if OIDC provider already exists
                      if (step.includes("create-open-id-connect-provider")) {
                        step = step + " 2>/dev/null || true";
                      }
                      return step;
                    })
                    .join(" && \\\n")}
                  language="bash"
                  className="my-2"
                />
                <li className="my-2 list-decimal">
                  Execute the following command to get the ARN for the new role, and paste it in the field below.
                </li>
                <SyntaxHighlight
                  code={`aws --no-cli-pager iam get-role --role-name ${roleName} --query 'Role.[RoleName, Arn]' --output text`}
                  language="bash"
                  className="my-2"
                />
              </>
            )}
          </ul>
        ) : null}
        <button type={"button"} className="block ngfocus color-link" onClick={() => setState("ui")}>
          How do I get my AWS Deploy Role ARN from AWS UI?{" "}
          {state === "ui" ? (
            <ChevronUp style={{ transform: "translateY(2px)" }} className="feather-icon inline-block" />
          ) : (
            <ChevronDown style={{ transform: "translateY(2px)" }} className="feather-icon inline-block" />
          )}
        </button>
        {state === "ui" ? (
          <ul className="mt-1 mb-4 px-4 py-2 ml-4">
            {/* Step 1: Navigate to IAM */}
            <li className="list-decimal my-2">
              Navigate to the{" "}
              <ExternalLink url="https://console.aws.amazon.com/iam" label="IAM Service" disableCopy />
              {" "}in AWS Console.
            </li>

            {/* Step 2: Add Identity Provider */}
            <li className="list-decimal my-2">
              Go to <span className="font-semibold">Identity providers</span> and add a new provider of type{" "}
              <span className="font-semibold">OpenID Connect</span>.
            </li>

            {/* Step 3: Configure provider */}
            <li className="list-decimal my-2">
              Configure the provider with URL as{" "}
              <span className="font-semibold">{`https://oidc.mk8s.${IS_DEPLOYMENT_ENV_TEST ? "test." : ""}cpln.io/federate`}</span>
              <CopyButtonWithApproval
                value={`https://oidc.mk8s.${IS_DEPLOYMENT_ENV_TEST ? "test." : ""}cpln.io/federate`}
                className="ml-1"
              />
              {" "}and audience as <span className="font-semibold">sts.amazonaws.com</span>
              <CopyButtonWithApproval value="sts.amazonaws.com" className="ml-1" />
              .
            </li>

            {/* Step 4: Add Provider */}
            <li className="list-decimal my-2">
              Click <span className="font-semibold">Add provider</span>.
            </li>

            {/* Step 5: Create Role */}
            <li className="list-decimal my-2">
              Go to <span className="font-semibold">Roles</span> and click <span className="font-semibold">Create role</span>.
              Select <span className="font-semibold">AWS Account</span> option.
            </li>
            <div className="flex items-center justify-center mb-2 border" style={{ width: 800 }}>
              <img
                className="object-container"
                style={{ width: 800 }}
                src="/resources/howto/aws-new-aws-account.png"
                alt="select the option aws account"
              />
            </div>

            {/* Step 6: Account ID input */}
            <li className="mb-2 list-decimal">
              Paste your AWS Account ID here (required for Trust Policy).
            </li>
            <Input
              style={{ width: 450 }}
              placeholder="AWS Account Id"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            />

            {accountId ? (
              <>
                {/* Step 7: Name and create role */}
                <li className="mb-2 list-decimal">
                  Optionally, customize the role name.
                </li>
                <Input
                  style={{ width: 450 }}
                  placeholder="Role Name"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                />
                <li className="my-2 list-decimal">
                  Click <span className="font-semibold">Next</span> twice, then name the role as{" "}
                  <span className="font-semibold">{roleName}</span>
                  <CopyButtonWithApproval value={roleName} className="ml-1" />
                  {" "}and click <span className="font-semibold">Create role</span>.
                </li>

                {/* Step 8: Create Policy */}
                <li className="list-decimal my-2">
                  Go to <span className="font-semibold">Policies</span> and click <span className="font-semibold">Create policy</span>.
                </li>

                {/* Step 9: Policy JSON */}
                <li className="list-decimal my-2">
                  Switch to the <span className="font-semibold">JSON</span> tab and paste the following connector policy:
                </li>
                <SyntaxHighlight
                  code={JSON.stringify(instructions.data.connectorPolicy, null, 2)}
                  language="json"
                  className="my-2"
                />

                {/* Step 10: Name and create policy */}
                <li className="list-decimal my-2">
                  Click <span className="font-semibold">Next</span>, name the policy as{" "}
                  <span className="font-semibold">{roleName}</span>
                  <CopyButtonWithApproval value={roleName} className="ml-1" />
                  {" "}and click <span className="font-semibold">Create policy</span>.
                </li>

                {/* Step 11: Select role and attach policy */}
                <li className="list-decimal my-2">
                  Go to <span className="font-semibold">Roles</span>, select the <span className="font-semibold">{roleName}</span> role,
                  click <span className="font-semibold">Add permissions → Attach policies</span>,
                  search for <span className="font-semibold">{roleName}</span>, and attach it.
                </li>
                <div className="flex items-center justify-center mb-2 border" style={{ width: 700 }}>
                  <img
                    className="object-container"
                    style={{ width: 700 }}
                    src="/resources/howto/aws-new-attach-policies.png"
                    alt="click attach policies under permission tab"
                  />
                </div>

                {/* Step 12: Update Trust Policy */}
                <li className="list-decimal my-2">
                  In the <span className="font-semibold">Trust relationships</span> tab, click{" "}
                  <span className="font-semibold">Edit trust policy</span> and paste the following:
                </li>
                <SyntaxHighlight
                  code={JSON.stringify(instructions.data.trustPolicy, null, 2).replaceAll("$ACCOUNT_ID", accountId)}
                  language="json"
                  className="my-2"
                />
                <li className="list-decimal my-2">
                  Click <span className="font-semibold">Update policy</span>.
                </li>

                {/* Step 13: Copy ARN */}
                <li className="list-decimal my-2">
                  Copy the <span className="font-semibold">Role ARN</span> from the role summary and paste it in the
                  Mk8s AWS Provider settings above.
                </li>
                <div className="flex items-center justify-center mb-2 border" style={{ width: 720 }}>
                  <img
                    className="object-container"
                    style={{ width: 842 }}
                    src="/resources/howto/aws_deploy_role_arn.png"
                    alt="copy deploy role arn"
                  />
                </div>
              </>
            ) : null}
          </ul>
        ) : null}
      </div>
    </>
  );
};
